import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Gift, Plus, Heart, DollarSign, CheckCircle2, Clock
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const Benefits = () => {
  const [benefits, setBenefits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [newBenefit, setNewBenefit] = useState({ name: "", description: "", category: "salud", value: "", provider: "" });

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const opts = { headers: headers(), withCredentials: true };
      const [bRes, rRes] = await Promise.all([
        axios.get(`${API}/benefits`, opts),
        axios.get(`${API}/benefit-requests`, opts).catch(() => ({ data: [] })),
      ]);
      setBenefits(bRes.data || []);
      setRequests(rRes.data || []);
    } catch { toast.error("Error al cargar beneficios"); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newBenefit.name) { toast.error("Nombre requerido"); return; }
    try {
      const data = { ...newBenefit };
      if (data.value) data.value = parseFloat(data.value);
      await axios.post(`${API}/benefits`, data, { headers: headers(), withCredentials: true });
      toast.success("Beneficio creado");
      setShowCreateDialog(false);
      setNewBenefit({ name: "", description: "", category: "salud", value: "", provider: "" });
      fetchData();
    } catch { toast.error("Error al crear beneficio"); }
  };

  const handleRequest = async () => {
    if (!selectedBenefit) return;
    try {
      await axios.post(`${API}/benefit-requests`, { benefit_id: selectedBenefit.benefit_id, benefit_name: selectedBenefit.name }, { headers: headers(), withCredentials: true });
      toast.success("Solicitud enviada");
      setShowRequestDialog(false);
      setSelectedBenefit(null);
      fetchData();
    } catch { toast.error("Error al solicitar"); }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.put(`${API}/benefit-requests/${requestId}/approve`, {}, { headers: headers(), withCredentials: true });
      toast.success("Solicitud aprobada");
      fetchData();
    } catch { toast.error("Error al aprobar"); }
  };

  const CATEGORY_ICONS = { salud: Heart, financiero: DollarSign, educacion: Gift, bienestar: Gift };
  const CATEGORY_COLORS = { salud: "bg-red-100 text-red-600", financiero: "bg-emerald-100 text-emerald-600", educacion: "bg-blue-100 text-blue-600", bienestar: "bg-violet-100 text-violet-600" };

  if (loading) return <Layout title="Beneficios"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Gestión de Beneficios" subtitle="Catálogo de beneficios y solicitudes"
      actions={
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild><Button data-testid="create-benefit-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo Beneficio</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo Beneficio</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nombre</Label><Input value={newBenefit.name} onChange={e => setNewBenefit({ ...newBenefit, name: e.target.value })} data-testid="benefit-name" /></div>
              <div><Label>Descripción</Label><Textarea rows={2} value={newBenefit.description} onChange={e => setNewBenefit({ ...newBenefit, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Categoría</Label>
                  <select className="form-input w-full mt-1" value={newBenefit.category} onChange={e => setNewBenefit({ ...newBenefit, category: e.target.value })}>
                    <option value="salud">Salud</option><option value="financiero">Financiero</option>
                    <option value="educacion">Educación</option><option value="bienestar">Bienestar</option>
                  </select>
                </div>
                <div><Label>Valor ($)</Label><Input type="number" value={newBenefit.value} onChange={e => setNewBenefit({ ...newBenefit, value: e.target.value })} /></div>
              </div>
              <div><Label>Proveedor</Label><Input value={newBenefit.provider} onChange={e => setNewBenefit({ ...newBenefit, provider: e.target.value })} /></div>
              <Button onClick={handleCreate} className="w-full" data-testid="save-benefit-btn">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Benefits Catalog */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Catálogo de Beneficios</h2>
          {benefits.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="benefits-empty">
              <Gift className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay beneficios</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map(b => {
                const Icon = CATEGORY_ICONS[b.category] || Gift;
                return (
                  <Card key={b.benefit_id} className="card-hover" data-testid={`benefit-${b.benefit_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${CATEGORY_COLORS[b.category] || "bg-slate-100"} flex items-center justify-center`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">{b.name}</p>
                          <p className="text-sm text-slate-500 mt-0.5">{b.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{b.category}</Badge>
                            {b.value && <span className="text-sm font-medium text-emerald-600">${b.value.toLocaleString()}</span>}
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => { setSelectedBenefit(b); setShowRequestDialog(true); }}>
                        Solicitar
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Requests */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Solicitudes</h2>
          {requests.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-slate-500"><Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">Sin solicitudes</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {requests.map(r => (
                <Card key={r.request_id} data-testid={`req-${r.request_id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{r.benefit_name || "Beneficio"}</p>
                        <p className="text-xs text-slate-500">{r.created_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100"}>{r.status}</Badge>
                        {r.status === "pending" && (
                          <Button size="sm" variant="ghost" onClick={() => handleApprove(r.request_id)}>
                            <CheckCircle2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Solicitar Beneficio</DialogTitle></DialogHeader>
          {selectedBenefit && (
            <div className="space-y-4">
              <p className="font-semibold">{selectedBenefit.name}</p>
              <p className="text-sm text-slate-500">{selectedBenefit.description}</p>
              <Button onClick={handleRequest} className="w-full" data-testid="confirm-request-btn">Confirmar Solicitud</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Benefits;
